// CreateAlbumDialog — modal form for creating a custom album (W3 §6.1).
//
// Per w3.md §5.1, name is required (1-100 chars), description is optional
// (≤2000 chars).  Server enforces uniqueness within the pair (case-insensitive)
// and rejects the reserved name 「未分類」 with HTTP 422.

import { useEffect, useState } from "react";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { useJsonDraft } from "@/lib/hooks/useDraft";
import { Button } from "@/components/ui/button";

export interface CreateAlbumDialogValues {
  name: string;
  description: string | null;
}

interface CreateAlbumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreateAlbumDialogValues) => Promise<void> | void;
  busy?: boolean;
  /**
   * W39.53 — the album list's 「重新命名」 row used to `console.warn` and do
   * nothing, while `PATCH /api/v1/albums/{id}` and `updateAlbum()` sat unused.
   * Rather than duplicate this form for editing, it takes its starting values
   * and its copy from props; omitting them keeps the original create behaviour.
   */
  initialName?: string;
  initialDescription?: string | null;
  title?: string;
  submitLabel?: string;
  submitBusyLabel?: string;
  /** Surfaces as `data-mode` so a test can tell the two modes apart. */
  mode?: "create" | "edit";
}

export function CreateAlbumDialog({
  open,
  onOpenChange,
  onSubmit,
  busy = false,
  initialName = "",
  initialDescription = null,
  title = "建立相簿",
  submitLabel = "建立",
  submitBusyLabel = "建立中…",
  mode = "create",
}: CreateAlbumDialogProps) {
  // W39.76 — measured, not assumed: the probe pointed at this dialog
  // (`/albums`, opener `albums-add`) reported `drafts parked: []` and an empty
  // description after a revoked session.
  //
  // The draft is the **whole form**, not just the long field. Drafting only the
  // description would bring the paragraph back and leave the name gone, which
  // looks like the save half-worked.
  const albumDraft = useJsonDraft("album-form", {
    name: initialName,
    description: initialDescription ?? "",
  });
  const [name, setName] = useState(albumDraft.value.name);
  const [description, setDescription] = useState(albumDraft.value.description);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName(initialName);
    setDescription(initialDescription ?? "");
    setError(null);
  };

  // Re-seed each time the sheet opens. Without this, opening the dialog for a
  // second album would show the *first* one's name, because `reset()` on close
  // puts the create-mode empty string back.
  //
  // A draft outranks the props, for the same reason as the daily message: a
  // draft only exists when someone was interrupted part-way through.
  useEffect(() => {
    if (open) {
      setName(albumDraft.value.name || initialName);
      setDescription(albumDraft.value.description || initialDescription || "");
      setError(null);
    }
    // Intentionally keyed on `open` only: re-seeding on every keystroke would
    // wipe what the user is typing, and `albumDraft.value` is read once anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError("請輸入相簿名稱");
      return;
    }
    if (trimmed === "未分類") {
      setError("此名稱為系統保留");
      return;
    }
    try {
      await onSubmit({
        name: trimmed,
        description: description.trim() ? description.trim() : null,
      });
      // Cleared only after the album exists on the server — clearing earlier is
      // what would make the draft useless for the case it exists for.
      albumDraft.clear();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "儲存失敗");
    }
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      variant="mobile"
      title={title}
      // W56.0 I — it is a form, so a stray tap on the backdrop must not throw
      // away what was typed. (The draft is the second line of defence; this is
      // the first.)
      dismissibleOnBackdrop={false}
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
        data-testid="create-album-dialog"
        data-mode={mode}
      >
        <label className="flex flex-col gap-1">
          <span className="text-body-sm text-ink-2">相簿名稱</span>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              albumDraft.save({ name: e.target.value, description });
            }}
            maxLength={100}
            placeholder="例如：旅行相冊"
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-body text-ink-1"
            data-testid="create-album-name"
            autoFocus
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-body-sm text-ink-2">說明（選填）</span>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              albumDraft.save({ name, description: e.target.value });
            }}
            maxLength={2000}
            rows={3}
            placeholder="一句話描述這本相簿的故事"
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-body text-ink-1 resize-none"
            data-testid="create-album-description"
          />
        </label>
        {error ? (
          <p
            className="text-body-sm text-partner-strong"
            data-testid="create-album-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            取消
          </Button>
          <Button type="submit" variant="primary" disabled={busy} data-testid="create-album-submit">
            {busy ? submitBusyLabel : submitLabel}
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}
